import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideAnalytics,
  provideAuth,
  provideFirebaseApp,
  provideFirestore,
  provideFunctions,
  providePerformance,
  provideResellerFirestorePaths,
  provideStorage,
  RoutePageTitleStrategy,
  ScreenTrackingService,
  UserTrackingService,
} from '@eneza/shared';

// Firebase imports
import { getAnalytics } from 'firebase/analytics';
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getPerformance } from 'firebase/performance';
import { getStorage } from 'firebase/storage';

// Application imports
import { provideHttpClient, withXhr } from '@angular/common/http';
import { TitleStrategy } from '@angular/router';
import { environment } from '@env/environment';
import { NgSelectModule } from '@ng-select/ng-select';
import { provideHotToastConfig } from '@ngxpert/hot-toast';
import { provideHighcharts } from 'highcharts-angular';
import { AppRoutingModule } from './app-routing.module';

export const appConfig: ApplicationConfig = {
  providers: [
    // Prefer signal UI state for async Firebase updates.
    // Firebase RxJS helpers also re-enter NgZone so plain-field subscribers still paint.
    // Remove zone.js once remaining plain-field screens are signal-driven.
    provideZoneChangeDetection(),
    provideHotToastConfig(),
    importProvidersFrom(BrowserModule, AppRoutingModule, NgSelectModule),

    // provide modular style for firebase, charts, etc
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAnalytics(() => getAnalytics()),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideFunctions(() => getFunctions()),
    providePerformance(() => getPerformance()),
    provideStorage(() => getStorage()),
    provideResellerFirestorePaths(),

    provideHighcharts({
      // ESM core must match ESM modules; CommonJS `highcharts` breaks module class extends
      instance: () => import('highcharts/esm/highcharts').then((m) => m.default),

      // Dependent modules (e.g. solid-gauge → highcharts-more) must load in a promise chain
      modules: () => {
        return [
          import('highcharts/esm/highcharts-more').then(
            () => import('highcharts/esm/modules/solid-gauge'),
          ),
          import('highcharts/esm/modules/accessibility'),
          import('highcharts/esm/modules/variable-pie'),
        ];
      },
    }),

    provideAnimationsAsync(),
    provideHttpClient(withXhr()),
    {
      provide: TitleStrategy,
      useClass: RoutePageTitleStrategy,
    },

    ScreenTrackingService,
    UserTrackingService,
  ],
};
